import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Checkbox,
  Space,
  message,
  InputNumber,
  Divider,
  Button,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { ChantButton } from "../../../../../CommonComponents";

const { TextArea } = Input;

const FIELD_TYPES = {
  Text: "text",
  Textarea: "textarea",
  Number: "number",
  Dropdown: "dropdown",
  Checkbox: "checkbox",
  Date: "date",
};

const EditCustomFieldModal = ({ showModal, data, onUpdateField, onClose }) => {
  const [form] = Form.useForm();
  const [fieldType, setFieldType] = useState("");
  const [dropdownValues, setDropdownValues] = useState([]);

  useEffect(() => {
    if (data && showModal) {
      form.setFieldsValue({
        fieldTitle: data.fieldTitle || "",
        fieldType: data.fieldType || "",
        required: data.required || false,
        fieldHelp: data.fieldHelp || "",
        maxChars: data.maxChars || "",
      });
      setFieldType(data.fieldType || "");
      setDropdownValues(data.dropdownValues || []);
    } else if (!showModal) {
      form.resetFields();
      setFieldType("");
      setDropdownValues([]);
    }
  }, [data, showModal, form]);

  const handleFieldTypeChange = (value) => {
    setFieldType(value);
  };

  const handleAddDropdownValue = () => {
    setDropdownValues([...dropdownValues, ""]);
  };

  const handleDropdownValueChange = (index, value) => {
    const newValues = [...dropdownValues];
    newValues[index] = value;
    setDropdownValues(newValues);
  };

  const handleRemoveDropdownValue = (index) => {
    const newValues = dropdownValues.filter((_, i) => i !== index);
    setDropdownValues(newValues);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // Additional validation for dropdown
      if (fieldType === FIELD_TYPES.Dropdown) {
        if (!dropdownValues || dropdownValues.length === 0) {
          message.error("Please add at least one dropdown value");
          return;
        }
        const hasEmptyValues = dropdownValues.some(
          (val) => !val || val.trim() === ""
        );
        if (hasEmptyValues) {
          message.error("Dropdown values cannot be empty");
          return;
        }
      }

      const updateObj = {
        ...values,
        dropdownValues:
          fieldType === FIELD_TYPES.Dropdown ? dropdownValues : undefined,
      };

      onUpdateField(updateObj);
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const renderFieldSpecificOptions = () => {
    switch (fieldType) {
      case FIELD_TYPES.Dropdown:
        return (
          <div>
            <Divider orientation="left" style={{ margin: "16px 0" }}>
              Dropdown Options
            </Divider>
            <Space direction="vertical" style={{ width: "100%" }}>
              {dropdownValues.map((value, index) => (
                <Space key={index} style={{ width: "100%", display: "flex" }}>
                  <Input
                    placeholder={`Option ${index + 1}`}
                    value={value}
                    onChange={(e) =>
                      handleDropdownValueChange(index, e.target.value)
                    }
                    style={{ flex: 1 }}
                  />
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveDropdownValue(index)}
                  />
                </Space>
              ))}
              <Button
                type="dashed"
                onClick={handleAddDropdownValue}
                icon={<PlusOutlined />}
                style={{ width: "100%" }}
              >
                Add Option
              </Button>
            </Space>
          </div>
        );

      case FIELD_TYPES.Text:
      case FIELD_TYPES.Textarea:
      case FIELD_TYPES.Number:
        return (
          <>
            <Divider orientation="left" style={{ margin: "16px 0" }}>
              Additional Options
            </Divider>
            <Form.Item
              name="fieldHelp"
              label="Help Text"
              tooltip="Instructions that will be shown to users filling out this field"
            >
              <TextArea
                placeholder="Enter help text or instructions"
                rows={2}
                maxLength={200}
                showCount
              />
            </Form.Item>
            <Form.Item
              name="maxChars"
              label="Max Characters"
              tooltip="Maximum number of characters allowed (max 50)"
            >
              <InputNumber
                min={1}
                max={50}
                placeholder="Enter max characters"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </>
        );

      case FIELD_TYPES.Date:
        return (
          <>
            <Divider orientation="left" style={{ margin: "16px 0" }}>
              Additional Options
            </Divider>
            <Form.Item
              name="fieldHelp"
              label="Help Text"
              tooltip="Instructions that will be shown to users filling out this field"
            >
              <TextArea
                placeholder="Enter help text or instructions"
                rows={2}
                maxLength={200}
                showCount
              />
            </Form.Item>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      title="Custom Field Configuration"
      open={showModal}
      onCancel={onClose}
      width={600}
      footer={[
        <ChantButton key="cancel" onClick={onClose} type="secondary">
          Cancel
        </ChantButton>,
        <ChantButton key="save" type="primary" onClick={handleSave}>
          Save Field
        </ChantButton>,
      ]}
    >
      <p style={{ color: "#666", marginBottom: "24px" }}>
        Create a custom field for your registration form. This field will be
        shown to users when they register.
      </p>

      <Form form={form} layout="vertical" autoComplete="off">
        <Form.Item
          name="fieldType"
          label="Field Type"
          rules={[{ required: true, message: "Please select a field type" }]}
        >
          <Select
            placeholder="Select field type"
            onChange={handleFieldTypeChange}
            options={Object.keys(FIELD_TYPES).map((label) => ({
              label,
              value: FIELD_TYPES[label],
            }))}
          />
        </Form.Item>

        <Form.Item
          name="fieldTitle"
          label="Field Name"
          rules={[
            { required: true, message: "Please enter a field name" },
            { max: 50, message: "Field name cannot exceed 50 characters" },
          ]}
        >
          <Input placeholder="Enter field name" maxLength={50} showCount />
        </Form.Item>

        <Form.Item name="required" valuePropName="checked">
          <Checkbox>Mark this field as required</Checkbox>
        </Form.Item>

        {renderFieldSpecificOptions()}
      </Form>
    </Modal>
  );
};

export default EditCustomFieldModal;
